import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { deletePtzAim, postPtzAim } from "@client/api/foscam";
import { foscamErrMsg } from "@client/lib/foscam/apiError";
import { t } from "@client/lib/dictionary";
import { useFoscamStore } from "@client/store/foscam";
import type { ImagePoint, Point } from "@client/lib/foscam/digitalZoom";

/**
 * Míření — otočení kamery tak, aby kliknuté místo skončilo uprostřed.
 *
 * Klient tady dělá jen tři věci: pošle cíl, drží značku v místě kliknutí a přeloží
 * výsledek do češtiny. Celá regulační smyčka (pulz → snímek → měření → korekce) běží na
 * serveru, protože mezi `ptzMove` a `ptzStopRun` se kamera fyzicky točí a nesmí to
 * záviset na tom, jestli zůstane otevřená záložka.
 *
 * Značka se kreslí v souřadnicích DLAŽDICE, ne obrazu: během otáčení se obraz pod ní
 * posouvá, takže kotvit ji k obrazu by znamenalo, že uteče z místa, kam uživatel klikl.
 */

/** Jak dlouho po dokončení zůstane značka a výsledek viditelný. */
const RESULT_LINGER_MS = 1_500;

export interface AimMarker {
  /** Pozice v px lokálně vůči rámu dlaždice. */
  at: Point;
  phase: "moving" | "measuring" | "done";
}

export interface UseAimApi {
  busy: boolean;
  marker: AimMarker | null;
  /** Spustí míření. Vrací `true`, když se požadavek vůbec odeslal. */
  aimAt: (point: ImagePoint, local: Point) => boolean;
}

export function useAim(slug: string, enabled: boolean): UseAimApi {
  const state = useFoscamStore((s) => s.aim[slug]);
  const beginAim = useFoscamStore((s) => s.beginAim);
  const clearAim = useFoscamStore((s) => s.clearAim);
  const [markerAt, setMarkerAt] = useState<Point | null>(null);
  const ownAimId = useRef<string | null>(null);
  const lingerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const aimAt = useCallback(
    (point: ImagePoint, local: Point): boolean => {
      if (!enabled) return false;
      setMarkerAt(local);
      void postPtzAim(slug, point.u, point.v)
        .then((res) => {
          const id = res.responseObject?.aimId;
          if (!id) return;
          ownAimId.current = id;
          beginAim(slug, id, point);
        })
        .catch((err: unknown) => {
          setMarkerAt(null);
          toast.error(foscamErrMsg(err));
        });
      return true;
    },
    [slug, enabled, beginAim]
  );

  // Výsledek → hláška + úklid značky. Jen u vlastního míření: cizí uživatel má svoje
  // hlášky u sebe a dvě toast zprávy o téže akci by byly matoucí.
  useEffect(() => {
    if (!state?.result || state.aimId !== ownAimId.current) return;
    const { status, limitDirection } = state.result;
    if (status === "limit") toast.warning(t.foscam.aimLimit(limitDirection ?? "Left"));
    else if (status === "unverified") toast.info(t.foscam.aimUnverified);
    else if (status === "partial") toast.info(t.foscam.aimPartial);

    if (lingerTimer.current) clearTimeout(lingerTimer.current);
    lingerTimer.current = setTimeout(() => {
      lingerTimer.current = null;
      setMarkerAt(null);
      ownAimId.current = null;
      clearAim(slug);
    }, RESULT_LINGER_MS);
  }, [state?.result, state?.aimId, slug, clearAim]);

  // Odchod z dlaždice (zavření spotlightu, přepnutí režimu) nesmí nechat kameru mířit
  // dál — uživatel už na to nekouká a nemá jak to zastavit.
  useEffect(
    () => () => {
      if (lingerTimer.current) clearTimeout(lingerTimer.current);
      if (ownAimId.current) void deletePtzAim(slug).catch(() => undefined);
    },
    [slug]
  );

  const phase = state?.phase ?? "done";
  const busy = state !== undefined && phase !== "done";

  return {
    busy,
    marker: markerAt ? { at: markerAt, phase } : null,
    aimAt,
  };
}
